import { useEffect, useState } from 'react';
import { Loader2, Plus, RefreshCw, ShieldCheck, ShieldAlert, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TagBadge } from '@/components/shared/TagBadge';
import { useRegistryApi } from '@/services/api';
import { vatStatusLabel, vatStatusPositive } from './vatStatus';
import { t } from '@/i18n';
import type {
  ContractorDetail,
  ContractorBankAccountData,
  ContractorRegistryRefreshResult,
  Tag as TagType,
} from '@ip/shared-types';

export interface ContractorEditForm {
  name: string;
  nip: string;
  erpId: string;
  street: string;
  postalCode: string;
  city: string;
  country: string;
  tags: string[];
  defaultInvoiceTagIds: string[];
}

interface ContractorEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contractor: ContractorDetail | null;
  companyNip: string;
  tags: TagType[];
  expenseTags: TagType[];
  saving: boolean;
  onSave: (form: ContractorEditForm, accounts: ContractorBankAccountData[]) => void;
  /** Called after a successful server-side registry refresh (list should reload). */
  onRefreshed?: () => void;
}

const EMPTY_FORM: ContractorEditForm = {
  name: '',
  nip: '',
  erpId: '',
  street: '',
  postalCode: '',
  city: '',
  country: '',
  tags: [],
  defaultInvoiceTagIds: [],
};

export function ContractorEditDialog({
  open,
  onOpenChange,
  contractor,
  companyNip,
  tags,
  expenseTags,
  saving,
  onSave,
  onRefreshed,
}: ContractorEditDialogProps) {
  const [form, setForm] = useState<ContractorEditForm>(EMPTY_FORM);
  const [structuredAccounts, setStructuredAccounts] = useState<ContractorBankAccountData[]>([]);
  const [fetchingRegistry, setFetchingRegistry] = useState(false);
  const [refreshResult, setRefreshResult] = useState<ContractorRegistryRefreshResult | null>(null);

  const { lookupRegistry, refreshContractorRegistry } = useRegistryApi();

  useEffect(() => {
    if (open && contractor) {
      setForm({
        name: contractor.name || '',
        nip: contractor.nip || '',
        erpId: contractor.erpId || '',
        street: contractor.street || '',
        postalCode: contractor.postalCode || '',
        city: contractor.city || '',
        country: contractor.country || '',
        tags: contractor.tags || [],
        defaultInvoiceTagIds: contractor.defaultInvoiceTagIds || [],
      });
      setStructuredAccounts(contractor.structuredBankAccounts || []);
      setRefreshResult(null);
    }
  }, [open, contractor]);

  const setField = (field: keyof ContractorEditForm, value: string) => {
    // Editing the NIP invalidates any registry answer shown for the old id.
    if (field === 'nip') setRefreshResult(null);
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateAccount = (index: number, patch: Partial<ContractorBankAccountData>) =>
    setStructuredAccounts((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], ...patch };
      return updated;
    });

  /**
   * For a SAVED contractor the refresh endpoint persists registry data and
   * returns the account comparison; for an unsaved/edited NIP fall back to a
   * read-only lookup that only fills the form.
   */
  const handleRegistryRefresh = async () => {
    if (!form.nip) {
      toast.warning(t('registry.refreshNoId'));
      return;
    }

    const nipUnchanged = contractor?.nip && form.nip.trim() === (contractor.nip || '');
    try {
      setFetchingRegistry(true);

      if (contractor?.id && nipUnchanged) {
        const result = await refreshContractorRegistry(companyNip, contractor.id);
        setRefreshResult(result);
        setForm((prev) => ({
          ...prev,
          name: result.contractor.name || prev.name,
          street: result.contractor.street || prev.street,
          postalCode: result.contractor.postalCode || prev.postalCode,
          city: result.contractor.city || prev.city,
          country: result.contractor.country || prev.country,
        }));
        toast.success(t('registry.refreshSuccess'));
        onRefreshed?.();
        return;
      }

      const data = await lookupRegistry(form.nip.trim());
      setForm((prev) => ({
        ...prev,
        ...(data.name ? { name: data.name } : {}),
        ...(data.street ? { street: data.street } : {}),
        ...(data.postalCode ? { postalCode: data.postalCode } : {}),
        ...(data.city ? { city: data.city } : {}),
        ...(data.country ? { country: data.country } : {}),
      }));
      toast.success(t('registry.lookupSuccess'));
    } catch (error) {
      const status = (error as { status?: number })?.status;
      toast.error(status === 404 ? t('registry.lookupNotFound') : t('registry.lookupFailed'));
    } finally {
      setFetchingRegistry(false);
    }
  };

  const toggleTag = (tagId: string, checked: boolean) =>
    setForm((prev) => ({
      ...prev,
      tags: checked ? [...prev.tags, tagId] : prev.tags.filter((id) => id !== tagId),
    }));

  const toggleDefaultInvoiceTag = (tagId: string, checked: boolean) =>
    setForm((prev) => ({
      ...prev,
      defaultInvoiceTagIds: checked
        ? [...prev.defaultInvoiceTagIds, tagId]
        : prev.defaultInvoiceTagIds.filter((id) => id !== tagId),
    }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('contractors.editModal.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Contractor */}
          <section className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">Contractor</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRegistryRefresh}
                disabled={fetchingRegistry}
              >
                <RefreshCw className={`size-3.5 ${fetchingRegistry ? 'animate-spin' : ''}`} />
                {t('registry.refresh')}
              </Button>
            </div>
            {(contractor?.vatStatus || refreshResult) && (
              <div className="flex flex-wrap items-center gap-1.5">
                {(() => {
                  const status = refreshResult?.registry.vatStatus ?? contractor?.vatStatus ?? null;
                  const verifiedAt =
                    refreshResult?.contractor.registryVerifiedAt ?? contractor?.registryVerifiedAt;
                  return (
                    <>
                      <Badge variant={vatStatusPositive(status) ? 'secondary' : 'destructive'}>
                        {vatStatusPositive(status) ? (
                          <ShieldCheck className="size-3" />
                        ) : (
                          <ShieldAlert className="size-3" />
                        )}
                        {vatStatusLabel(status) ?? t('registry.neverVerified')}
                      </Badge>
                      {verifiedAt && (
                        <span className="text-muted-foreground text-xs">
                          {t('registry.verifiedAt', {
                            date: new Date(verifiedAt).toLocaleDateString(),
                          })}
                        </span>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
            {refreshResult?.accountComparison && (
              <div className="rounded-md border p-2 text-xs space-y-1">
                <p className="font-semibold">{t('registry.accountsTitle')}</p>
                {refreshResult.accountComparison.notOnWhitelist.length > 0 && (
                  <p className="text-destructive">
                    {t('registry.accountsNotOnWhitelist')}:{' '}
                    {refreshResult.accountComparison.notOnWhitelist.join(', ')}
                  </p>
                )}
                {refreshResult.accountComparison.missingLocally.length > 0 && (
                  <p className="text-muted-foreground">
                    {t('registry.accountsMissingLocally')}:{' '}
                    {refreshResult.accountComparison.missingLocally.join(', ')}
                  </p>
                )}
                {refreshResult.accountComparison.notOnWhitelist.length === 0 &&
                  refreshResult.accountComparison.matched.length > 0 && (
                    <p className="text-green-600 dark:text-green-500">
                      {t('registry.accountsAllMatched')}
                    </p>
                  )}
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs">{t('contractors.editModal.name')}</Label>
              <Input
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                placeholder="Contractor name"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t('contractors.editModal.nip')}</Label>
              <Input
                value={form.nip}
                onChange={(e) => setField('nip', e.target.value)}
                placeholder="Tax ID"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t('contractors.editModal.erpId')}</Label>
              <Input
                value={form.erpId}
                onChange={(e) => setField('erpId', e.target.value)}
                placeholder={t('contractors.editModal.erpIdPlaceholder')}
                maxLength={100}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Street</Label>
              <Input
                value={form.street}
                onChange={(e) => setField('street', e.target.value)}
                placeholder="Street address"
              />
            </div>
            <div className="grid grid-cols-[1fr_1.5fr_1fr] gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Postal</Label>
                <Input
                  value={form.postalCode}
                  onChange={(e) => setField('postalCode', e.target.value)}
                  placeholder="00-000"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">City</Label>
                <Input
                  value={form.city}
                  onChange={(e) => setField('city', e.target.value)}
                  placeholder="City"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Country</Label>
                <Input
                  value={form.country}
                  onChange={(e) => setField('country', e.target.value)}
                  placeholder="PL"
                />
              </div>
            </div>
          </section>

          {/* Bank Accounts */}
          <section className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">Bank Accounts</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setStructuredAccounts((prev) => [
                    ...prev,
                    { id: crypto.randomUUID(), iban: '', swiftBic: null, currency: null, label: null },
                  ])
                }
              >
                <Plus className="size-3.5" />
                Add
              </Button>
            </div>
            {structuredAccounts.length === 0 ? (
              <p className="text-muted-foreground text-sm">No bank accounts</p>
            ) : (
              structuredAccounts.map((acct, idx) => (
                <div key={acct.id} className="rounded-md border p-2 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold">Account {idx + 1}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive size-7"
                      onClick={() =>
                        setStructuredAccounts((prev) => prev.filter((_, i) => i !== idx))
                      }
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                  <Input
                    value={acct.iban}
                    onChange={(e) => updateAccount(idx, { iban: e.target.value })}
                    placeholder="IBAN"
                  />
                  <div className="grid grid-cols-[1.4fr_1fr_1fr] gap-1.5">
                    <Input
                      value={acct.swiftBic || ''}
                      onChange={(e) =>
                        updateAccount(idx, { swiftBic: e.target.value.toUpperCase() || null })
                      }
                      placeholder="SWIFT/BIC"
                      maxLength={11}
                    />
                    <Input
                      value={acct.currency || ''}
                      onChange={(e) =>
                        updateAccount(idx, { currency: e.target.value.toUpperCase() || null })
                      }
                      placeholder="EUR"
                      maxLength={3}
                    />
                    <Input
                      value={acct.label || ''}
                      onChange={(e) => updateAccount(idx, { label: e.target.value || null })}
                      placeholder="Label"
                    />
                  </div>
                </div>
              ))
            )}
          </section>

          {/* Additional Info */}
          <section className="space-y-2">
            <h3 className="text-sm font-semibold">Additional Info</h3>
            <div className="space-y-1">
              <Label className="text-xs">{t('contractors.editModal.tags')}</Label>
              <div className="max-h-48 space-y-2 overflow-y-auto">
                {tags.map((tag) => (
                  <Label key={tag.id} className="flex items-center gap-2 py-0.5 font-normal">
                    <Checkbox
                      checked={form.tags.includes(tag.id)}
                      onCheckedChange={(checked) => toggleTag(tag.id, checked === true)}
                    />
                    <TagBadge name={tag.name} color={tag.color} />
                  </Label>
                ))}
                {tags.length === 0 && (
                  <p className="text-muted-foreground text-sm">No tags available</p>
                )}
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t('contractors.defaultInvoiceTags')}</Label>
              <p className="text-muted-foreground text-xs">
                {t('contractors.defaultInvoiceTagsHint')}
              </p>
              <div className="max-h-48 space-y-2 overflow-y-auto">
                {expenseTags.map((tag) => (
                  <Label key={tag.id} className="flex items-center gap-2 py-0.5 font-normal">
                    <Checkbox
                      checked={form.defaultInvoiceTagIds.includes(tag.id)}
                      onCheckedChange={(checked) =>
                        toggleDefaultInvoiceTag(tag.id, checked === true)
                      }
                    />
                    <TagBadge name={tag.name} color={tag.color} />
                  </Label>
                ))}
                {expenseTags.length === 0 && (
                  <p className="text-muted-foreground text-sm">No tags available</p>
                )}
              </div>
            </div>
          </section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button disabled={saving} onClick={() => onSave(form, structuredAccounts)}>
            {saving && <Loader2 className="size-4 animate-spin" />}
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
